import { useEffect, useState } from 'react';
import { Button, Caption, Section, Title } from '@telegram-apps/telegram-ui';
import { BoxCodeView } from '@veylme/shared';

import { apiFetch } from '../api/client.js';
import { SkeletonRows } from './Skeleton.js';
import { RotateConfirmSheet } from './RotateConfirmSheet.js';
import { useT } from '../hooks/useT.js';
import styles from './MyCodePanel.module.css';

/**
 * MyCodePanel — секция «Ваш код для сторис» в Настройках (work-020, решение 7).
 *
 * Только для НЕ-Premium авторов (треб. 1: Premium ничего про коды не видит —
 * родитель `SettingsScreen` не рендерит панель для Premium). На mount →
 * `POST /boxes/code` (ленивая идемпотентная выдача своего кода) → показ кода +
 * подсказка + «Получить новый код» (`POST /boxes/code/rotate` → старый код
 * гасится, выдаётся новый).
 *
 * Код-показ — трактовка «крупный код» (DESIGN §3): kit `Title` (компактный
 * вариант для секции) + моноширинность + разрядка. Анти-дабл-клик — гард `busy`.
 */

type CodeState =
  | { kind: 'loading' }
  | { kind: 'ready'; code: string }
  | { kind: 'error' };

export function MyCodePanel() {
  const t = useT();
  const [state, setState] = useState<CodeState>({ kind: 'loading' });
  // Гард анти-дабл-клик ротации + транзиентный статус «обновлён».
  const [rotating, setRotating] = useState(false);
  const [justRotated, setJustRotated] = useState(false);
  // Гейт подтверждения перед необратимой сменой кода (work-033): смена гасит
  // старый код навсегда и рвёт уже опубликованные сторис-ссылки.
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const view = await apiFetch('/boxes/code', {
          method: 'POST',
          body: {},
          parse: BoxCodeView,
        });
        if (!cancelled) setState({ kind: 'ready', code: view.code });
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleRotate() {
    if (rotating) return;
    setRotating(true);
    setJustRotated(false);
    try {
      const view = await apiFetch('/boxes/code/rotate', {
        method: 'POST',
        body: {},
        parse: BoxCodeView,
      });
      setState({ kind: 'ready', code: view.code });
      setJustRotated(true);
      setTimeout(() => setJustRotated(false), 1500);
    } catch {
      setState({ kind: 'error' });
    } finally {
      setRotating(false);
    }
  }

  return (
    <>
    <Section header={t.settings_my_code_title}>
      {state.kind === 'loading' && (
        <SkeletonRows count={1} withLead={false} />
      )}

      {state.kind === 'error' && (
        <div className={styles.row}>
          <Caption Component="p" className={styles.errorText}>
            {t.settings_my_code_error}
          </Caption>
        </div>
      )}

      {state.kind === 'ready' && (
        <div className={styles.row}>
          <Title level="3" weight="2" className={styles.code}>
            {state.code}
          </Title>
          <Caption Component="p" level="1" className={styles.hint}>
            {t.settings_my_code_hint}
          </Caption>
          <Button
            mode="bezeled"
            size="m"
            stretched
            loading={rotating}
            disabled={rotating}
            onClick={() => setConfirming(true)}
          >
            {rotating
              ? t.settings_my_code_rotating
              : justRotated
                ? t.settings_my_code_done
                : t.settings_my_code_rotate}
          </Button>
        </div>
      )}
    </Section>
    <RotateConfirmSheet
      open={confirming}
      onConfirm={() => {
        setConfirming(false);
        void handleRotate();
      }}
      onCancel={() => setConfirming(false)}
    />
    </>
  );
}
